import type { Epic } from '../../index.js'
import type { Action } from '@reduxjs/toolkit'
import { filter, mergeMap, Observable, takeUntil, withLatestFrom } from 'rxjs'
import { chatSlice, selectConversationId, selectPinnedItemIds } from '../../Slice/Chat/ChatSlice.js'
import type { ChatTurn } from '../../../Generated/ChatTurn.ts'
import { isHttpStreamError } from '../../../View/Page/OrderManagementPage/Modals/Krona/Streaming/HttpStreamError.ts'
import { buildVizContext } from './vizContext.js'

/** Max prior turns sent as history (drop oldest). Mirrors the backend cap (ChatController.HISTORY_CAP). */
const HISTORY_CAP = 12

/**
 * Drives a single chat turn over SSE: POST the message to the streaming agent-loop endpoint and fold the
 * live events (tool steps, then answer deltas, then the terminal canvas payload) into the conversation via
 * the incremental slice actions. `mergeMap` (not `switchMap`) so an in-flight answer is not dropped if the
 * user fires a second question — each turn is independent (the backend is stateless per call).
 *
 * History: the server is stateless, so we send the prior prose turns for multi-turn context. The
 * `sendMessageRequest` reducer has already pushed the current user turn AND an empty streaming assistant
 * placeholder, so we drop the placeholder (any `streaming` message) and the current user turn before
 * sending. Only user/assistant prose turns go over the wire — never the tool-call step traces.
 */
export const SendMessageEpic: Epic = (action$, state$, { ChatRepository, [chatSlice.name]: chat }) =>
  action$.pipe(
    filter(chat.sendMessageRequest.match),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const priorTurns = (state.chat?.messages ?? []).filter((m) => !m.streaming).slice(0, -1)
      const history: ChatTurn[] = priorTurns.map((m): ChatTurn => ({ role: m.role, content: m.content })).slice(-HISTORY_CAP)
      // Only send `citeSources` when the toggle is ON; omit it when off so the backend falls back to its
      // provider default (Claude rich / Qwen lean) rather than being explicitly told not to cite.
      const citeSources = state.chat?.citeSources === true
      // W-2b: send which workspace items the user has PINNED so the backend report-edit tools refuse to touch
      // a pinned report (locked). Omit when nothing is pinned so the request stays lean.
      const pinnedItemIds = selectPinnedItemIds(state)
      // Stable per-session id → the backend's conversation-scoped tool-call cache (partitioned server-side by
      // the authenticated user). Always sent; a null would just disable caching for the turn.
      const conversationId = selectConversationId(state)
      // The assistant placeholder this turn writes to. `sendMessageRequest` has already appended it (epics see
      // post-reducer state), so pin its id and thread it through every stream action: a late event from a
      // superseded turn then targets its OWN node (dropped if that node is gone) instead of folding into
      // whatever turn is current now — the scope-switch / branch-switch cross-talk bug.
      const turnNodeId = state.chat?.currentNodeId ?? undefined

      return new Observable<Action>((subscriber) => {
        const controller = new AbortController()
        let settled = false

        ChatRepository.streamChat(
          {
            scopeType: action.payload.scopeType,
            scopeId: action.payload.scopeId,
            message: action.payload.message,
            vizContext: buildVizContext(state),
            history,
            conversationId,
            ...(citeSources ? { citeSources: true } : {}),
            ...(pinnedItemIds.length > 0 ? { pinnedItemIds } : {})
          },
          {
            signal: controller.signal,
            onStep: (event) =>
              subscriber.next(
                chat.addStreamStep({
                  nodeId: turnNodeId,
                  step: { tool: event.tool ?? '', args: event.args, resultPreview: event.resultPreview ?? '' }
                })
              ),
            onDelta: (text) => subscriber.next(chat.appendAnswerDelta({ nodeId: turnNodeId, text })),
            onDone: (event) => {
              settled = true
              subscriber.next(
                chat.finishStream({
                  nodeId: turnNodeId,
                  directives: event.directives,
                  focusChartRef: event.focusChartRef,
                  chartUpdate: event.chartUpdate,
                  comparisonCaveat: event.comparisonCaveat,
                  resolvedContext: event.resolvedContext,
                  evidence: event.evidence,
                  renderedImages: event.renderedImages,
                  renderedReports: event.renderedReports,
                  pinRefusals: event.pinRefusals
                })
              )
              // IR-1: fold this turn's report directives into currentReport. The RenderReportEpic reacts to
              // this action and issues ONE server render off the resulting blocks. Empty/absent = no report work.
              if (event.reportEdits && event.reportEdits.length > 0) {
                subscriber.next(chat.applyReportEdits(event.reportEdits))
              }
              subscriber.complete()
            },
            onError: () => {
              // In-band engine failure — same case as the non-streaming 503 path.
              settled = true
              subscriber.next(chat.failStream({ nodeId: turnNodeId, status: 503 }))
              subscriber.complete()
            }
          }
        )
          .then(() => {
            // Stream closed without an explicit done/error (e.g. truncation) — close the turn cleanly.
            if (!settled) subscriber.next(chat.failStream({ nodeId: turnNodeId, status: undefined }))
            subscriber.complete()
          })
          .catch((error: unknown) => {
            if (!settled) {
              const status = isHttpStreamError(error) ? error.status : undefined
              subscriber.next(chat.failStream({ nodeId: turnNodeId, status }))
            }
            subscriber.complete()
          })

        return () => controller.abort()
      }).pipe(
        // Tear down this turn's SSE fetch when the user stops it (`cancelStream`, item #8) OR navigates to a
        // different scope (`resetConversation`): unsubscribing fires the teardown → `controller.abort()`. The
        // `cancelStream` reducer closes the placeholder cleanly (stopped, not errored); on `resetConversation`
        // the conversation is already cleared, so the abort just stops wasted work. Any post-abort `failStream`
        // from the fetch rejection is a no-op since we've already unsubscribed.
        takeUntil(action$.pipe(filter((a) => chat.cancelStream.match(a) || chat.resetConversation.match(a))))
      )
    })
  )
